import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { Clapperboard, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSong } from '@/components/landing/song-provider'
import { VIDEOS } from '@/constants/birthday'

const CLIPS = VIDEOS.clips.items

// One video element plays all 3 clips in order, so the next clip can start on its own.
const ClipsPlayer: FC = (): ReactNode => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const [index, setIndex] = useState(0)
    const [ratio, setRatio] = useState(9 / 16)
    const [failed, setFailed] = useState(false)
    const { pause: pauseSong } = useSong()

    const playClip = (next: number): void => {
        const video = videoRef.current

        if (!video) return

        setIndex(next)
        setFailed(false)

        if (next !== index || video.ended) {
            video.src = CLIPS[next].src
            video.load()
        }
        else {
            video.currentTime = 0
        }

        pauseSong()
        video.play().catch(() => undefined)
    }

    return (
        <div className='grid items-center gap-10 md:grid-cols-[0.95fr_1.05fr] lg:gap-16'>
            <div className='text-center md:text-left'>
                <span className='inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.2em] text-rose-300'>
                    <Clapperboard className='size-4' />
                    {VIDEOS.clips.count}
                </span>

                <h3 className='mt-5 font-heading text-3xl font-semibold text-white text-balance sm:text-4xl'>
                    {VIDEOS.clips.title}
                </h3>

                <p className='mt-4 text-base leading-relaxed text-white/75 text-pretty sm:text-lg'>
                    {VIDEOS.clips.subtitle}
                </p>

                <button
                    type='button'
                    onClick={() => playClip(index)}
                    className='mt-8 inline-flex items-center gap-2.5 rounded-full bg-primary px-7 py-3.5 font-bold text-primary-foreground shadow-lg shadow-black/30 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/40'
                >
                    <Play className='size-5 fill-current' />
                    {VIDEOS.clips.play}
                </button>

                <div role='group' aria-label={VIDEOS.clips.count} className='mt-6 flex flex-wrap justify-center gap-2 md:justify-start'>
                    {CLIPS.map((clip, clipIndex) => (
                        <button
                            key={clip.src}
                            type='button'
                            aria-pressed={clipIndex === index}
                            onClick={() => playClip(clipIndex)}
                            className={cn(
                                'rounded-full px-4 py-2 text-sm font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
                                clipIndex === index ? 'bg-white text-wine' : 'bg-white/10 text-white hover:bg-white/20'
                            )}
                        >
                            {VIDEOS.clips.label} {clipIndex + 1}
                        </button>
                    ))}
                </div>
            </div>

            <div
                className='relative mx-auto overflow-hidden rounded-[2rem] bg-black shadow-2xl shadow-black/40 ring-1 ring-white/10'
                style={{ aspectRatio: ratio, width: `min(100%, ${(ratio * 70).toFixed(2)}vh)` }}
            >
                <video
                    ref={videoRef}
                    src={`${CLIPS[0].src}#t=0.1`}
                    aria-label={VIDEOS.clips.title}
                    controls
                    playsInline
                    preload='metadata'
                    className='size-full bg-black object-contain'
                    onPlay={() => pauseSong()}
                    onLoadedMetadata={(event) => {
                        const { videoWidth, videoHeight } = event.currentTarget

                        if (videoWidth > 0 && videoHeight > 0) setRatio(videoWidth / videoHeight)
                    }}
                    onEnded={() => {
                        if (index < CLIPS.length - 1) playClip(index + 1)
                    }}
                    onError={() => setFailed(true)}
                />

                <span className='pointer-events-none absolute left-4 top-4 rounded-full bg-black/55 px-3 py-1 text-xs font-bold text-white backdrop-blur'>
                    {VIDEOS.clips.label} {index + 1} / {CLIPS.length}
                </span>

                {failed && (
                    <p role='alert' className='absolute inset-x-4 bottom-16 rounded-2xl bg-black/70 px-4 py-3 text-center text-sm font-semibold text-white'>
                        {VIDEOS.clips.error}
                    </p>
                )}
            </div>
        </div>
    )
}

export default ClipsPlayer
